import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, CalendarDays, History, Loader2, Lock, MessageSquarePlus,
  Pencil, ShieldAlert, StickyNote, UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmploymentStatusBadge } from "@/pages/shared/EmploymentList";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const EVENT_LABELS = {
  EMPLOYMENT_CREATED: "Employment created",
  JOINED: "Joined",
  LEFT: "Left",
  LOCK_COMPLETE: "Lock complete",
  READY_FOR_INVOICE: "Ready for invoice",
  STATUS_CHANGE: "Status change",
  REMARK: "Remark",
  ADMIN_NOTE: "Admin note",
  FIELD_CHANGE: "Field change",
};

export default function EmploymentRecord() {
  const { empId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const basePath = isAdmin ? "/admin" : "/recruiter";

  const [rec, setRec] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null); // M05: coarse read-only status for recruiters
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const [remark, setRemark] = useState("");
  const [note, setNote] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [timeline, setTimeline] = useState([]);

  const load = useCallback(() => {
    api.get(`/employment/${empId}`)
      .then((res) => setRec(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
    api.get(`/employment/${empId}/timeline`)
      .then((res) => setTimeline(res.data.entries || []))
      .catch(() => {});
  }, [empId]);

  useEffect(() => {
    load();
    api.get("/master-data").then((res) => setStatuses(res.data.employment_record_status || [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    // M05: recruiters see only a minimal billing status — no invoice details.
    if (!isAdmin && empId) {
      api.get(`/billing/status/${empId}`).then((res) => setBillingStatus(res.data.billing_status)).catch(() => {});
    }
  }, [isAdmin, empId, rec?.status]);

  const submitStatus = async () => {
    if (!newStatus) return toast.error("Choose a status");
    setBusy(true);
    try {
      const payload = { status: newStatus };
      if (reason.trim()) payload.reason = reason.trim();
      if (override) {
        payload.override = true;
        payload.override_reason = overrideReason.trim();
      }
      const res = await api.put(`/employment/${empId}/status`, payload);
      setRec(res.data);
      setStatusOpen(false);
      setNewStatus(""); setReason(""); setOverride(false); setOverrideReason("");
      toast.success(`Status changed to ${res.data.status}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitRemark = async () => {
    if (remark.trim().length < 2) return toast.error("Write a remark first");
    setBusy(true);
    try {
      const res = await api.post(`/employment/${empId}/remarks`, { text: remark.trim() });
      setRec(res.data);
      setRemark("");
      api.get(`/employment/${empId}/timeline`).then((r) => setTimeline(r.data.entries || [])).catch(() => {});
      toast.success("Follow-up remark added");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async () => {
    if (note.trim().length < 2) return toast.error("Write a note first");
    setBusy(true);
    try {
      const res = await api.post(`/employment/${empId}/notes`, { text: note.trim() });
      setRec(res.data);
      setNote("");
      api.get(`/employment/${empId}/timeline`).then((r) => setTimeline(r.data.entries || [])).catch(() => {});
      toast.success("Admin note added");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of ["date_of_joining", "date_selected", "remarks"]) {
        if ((edit[k] || "") !== (rec[k] || "")) payload[k] = edit[k];
      }
      if (Object.keys(payload).length === 0) { setEditOpen(false); return; }
      const res = await api.put(`/employment/${empId}`, payload);
      setRec(res.data);
      setEditOpen(false);
      toast.success("Employment record updated");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  }
  if (!rec) {
    return <p className="text-sm text-muted-foreground">Employment record not found. <Link to={`${basePath}/employment`} className="text-primary hover:underline">Back to Employment Register</Link></p>;
  }

  const closed = rec.status === "Left";

  return (
    <div className="space-y-6">
      <Link to={`${basePath}/employment`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="employment-back-link">
        <ArrowLeft className="h-4 w-4" /> Employment Register
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <span className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <UserRound className="h-7 w-7" />
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight" data-testid="employment-candidate-name">
            {rec.candidate?.full_name || "—"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="font-mono text-xs" data-testid="employment-code">{rec.emp_code}</span>
            {rec.candidate?.code && <Link to={`${basePath}/candidates/${rec.candidate.id}`} className="font-mono text-xs text-primary hover:underline">{rec.candidate.code}</Link>}
            {rec.application?.app_code && <Link to={`${basePath}/applications/${rec.application.id}`} className="font-mono text-xs text-primary hover:underline" data-testid="employment-application-link">{rec.application.app_code}</Link>}
            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{rec.company?.name || "—"}</span>
            <span>{rec.job?.title}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <EmploymentStatusBadge status={rec.status} className="text-sm px-3 py-1" />
          {!isAdmin && billingStatus && (
            <span className="inline-flex rounded-full bg-muted text-muted-foreground border border-border px-3 py-1 text-xs font-medium" data-testid="recruiter-billing-status">
              Billing: {billingStatus}
            </span>
          )}
          {isAdmin && !closed && (
            <>
              <Button onClick={() => setStatusOpen(true)} className="rounded-lg" data-testid="employment-change-status-button">
                Change Status
              </Button>
              <Button variant="outline" className="rounded-lg bg-white" data-testid="employment-edit-button"
                onClick={() => { setEdit({ date_of_joining: rec.date_of_joining || "", date_selected: rec.date_selected || "", remarks: rec.remarks || "" }); setEditOpen(true); }}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 min-w-0 space-y-6">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Employment summary</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <p><span className="text-muted-foreground">Date selected:</span> <span className="font-medium">{formatDate(rec.date_selected)}</span></p>
              <p><span className="text-muted-foreground">Date of joining:</span> <span className="font-medium" data-testid="employment-joining-date">{formatDate(rec.date_of_joining)}</span></p>
              <p><span className="text-muted-foreground">Locking period:</span> <span className="font-medium" data-testid="employment-locking-label">{rec.locking_template_label} ({rec.locking_value} {rec.locking_unit})</span></p>
              <p><span className="text-muted-foreground">Locking end date:</span> <span className="font-medium" data-testid="employment-locking-end">{formatDate(rec.locking_end_date)}</span></p>
              <p><span className="text-muted-foreground">Recruiter:</span> <span className="font-medium">{rec.recruiter?.name || "—"}</span></p>
              {rec.closed_at && <p><span className="text-muted-foreground">Closed:</span> <span className="font-medium">{formatDateTime(rec.closed_at)}</span></p>}
              {rec.remarks && <p className="sm:col-span-2 text-muted-foreground border-t border-border pt-2 whitespace-pre-line">{rec.remarks}</p>}
            </CardContent>
          </Card>

          <Tabs defaultValue="history">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="history" data-testid="employment-tab-history">History ({(rec.history || []).length})</TabsTrigger>
              <TabsTrigger value="trail" data-testid="employment-tab-trail">Notes Trail ({timeline.length})</TabsTrigger>
              {isAdmin && <TabsTrigger value="notes" data-testid="employment-tab-notes">Admin Notes ({(rec.admin_notes || []).length})</TabsTrigger>}
            </TabsList>

            <TabsContent value="trail" className="mt-4">
              <Card className="rounded-xl border-border shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><MessageSquarePlus className="h-4 w-4 text-primary" /> Unified notes trail</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {isAdmin
                      ? "Admin notes, follow-up remarks and milestone confirmations — one chronological trail."
                      : "Follow-up remarks and milestone confirmations — one chronological trail."}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2.5" data-testid="employment-notes-trail">
                  {[...timeline].reverse().map((t, i) => (
                    <div key={`${t.at}-${i}`} className="rounded-lg border border-border p-3" data-testid="trail-item">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          t.kind === "admin_note" ? "bg-amber-100 text-amber-800"
                            : t.kind === "milestone_remark" ? "bg-emerald-100 text-emerald-800"
                            : t.kind === "milestone_note" ? "bg-slate-100 text-slate-600"
                            : "bg-primary/10 text-primary"}`}>
                          {t.context || "Remark"}
                        </span>
                        <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(t.at)}</span>
                      </div>
                      <p className="text-sm mt-1.5 whitespace-pre-line">{t.text}</p>
                      {t.by && <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{t.by}</span></p>}
                    </div>
                  ))}
                  {timeline.length === 0 && <p className="text-sm text-muted-foreground">No notes or remarks yet.</p>}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card className="rounded-xl border-border shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Employment history</CardTitle>
                  <p className="text-xs text-muted-foreground">Immutable event log — never edited or deleted.</p>
                </CardHeader>
                <CardContent className="space-y-2.5" data-testid="employment-event-history">
                  {[...(rec.history || [])].reverse().map((e) => (
                    <div key={e.id} className="rounded-lg border border-border p-3" data-testid="employment-history-item">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="flex items-center gap-2">
                          <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            {EVENT_LABELS[e.event_type] || e.event_type}
                          </span>
                          {e.override && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="override-badge">
                              <ShieldAlert className="h-3 w-3" /> Override
                            </span>
                          )}
                        </span>
                        <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                      </div>
                      {(e.old !== null || e.new !== null) && (e.old !== undefined || e.new !== undefined) && (
                        <p className="text-sm mt-1.5">
                          {e.field && <span className="text-muted-foreground">{(e.field || "").replaceAll("_", " ")}: </span>}
                          {e.old ?? "—"}<span className="text-muted-foreground mx-1.5">→</span>
                          <span className="font-medium">{e.new ?? "—"}</span>
                        </p>
                      )}
                      {e.remarks && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                      <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span>{e.actor_role && e.actor_role !== "system" ? ` (${(e.actor_role || "").replaceAll("_", " ")})` : ""}</p>
                    </div>
                  ))}
                  {(rec.history || []).length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
                </CardContent>
              </Card>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="notes" className="mt-4">
                <Card className="rounded-xl border-border shadow-none">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-heading flex items-center gap-2"><StickyNote className="h-4 w-4 text-primary" /> Admin notes</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Textarea data-testid="admin-note-input" rows={2} placeholder="Internal admin note (append-only)..." value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                    <Button size="sm" onClick={submitNote} disabled={busy} className="rounded-lg" data-testid="admin-note-submit">
                      {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Add note
                    </Button>
                    <div className="space-y-2">
                      {[...(rec.admin_notes || [])].reverse().map((n) => (
                        <div key={n.id} className="rounded-lg bg-muted/50 border border-border p-3" data-testid="admin-note-item">
                          <p className="text-sm whitespace-pre-line">{n.text}</p>
                          <p className="text-[11px] text-muted-foreground mt-1">{n.by} · {formatDateTime(n.at)}</p>
                        </div>
                      ))}
                      {(rec.admin_notes || []).length === 0 && <p className="text-sm text-muted-foreground">No admin notes yet.</p>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        <div className="space-y-6 min-w-0">
          <Card className="rounded-xl border-primary/20 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <MessageSquarePlus className="h-4 w-4 text-primary" /> Follow-up remarks
              </CardTitle>
              <p className="text-xs text-muted-foreground">{isAdmin ? "Visible to the whole team." : "The only update recruiters can add."}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea data-testid="followup-remark-input" rows={3} placeholder="Add a follow-up remark..." value={remark} onChange={(e) => setRemark(e.target.value)} />
              <Button size="sm" onClick={submitRemark} disabled={busy} className="w-full rounded-lg" data-testid="followup-remark-submit">
                {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Add remark
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> Key dates</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1.5">
              <p><span className="text-muted-foreground">Created:</span> <span className="font-medium">{formatDateTime(rec.created_at)}</span></p>
              <p><span className="text-muted-foreground">Last update:</span> <span className="font-medium">{formatDateTime(rec.updated_at)}</span></p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Status change dialog with enforced flow + override */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="employment-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Change employment status</DialogTitle>
            <DialogDescription>
              Enforced flow: Joined → Invoice Pending (automatic on the lock-end date) → Payment Received. “Left” allowed from Joined/Invoice Pending.
              Out-of-flow changes need an override with a mandatory reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>New status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="employment-status-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {statuses.filter((s) => s !== rec.status).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reason / note</Label>
              <Textarea data-testid="employment-status-reason-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional context..." />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="employment-override-checkbox" />
              Override enforced flow (admin)
            </label>
            {override && (
              <div className="space-y-1.5">
                <Label>Override reason <span className="text-destructive">*</span></Label>
                <Textarea data-testid="employment-override-reason-input" rows={2} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Mandatory — recorded permanently in the audit log" />
              </div>
            )}
            <Button onClick={submitStatus} disabled={busy} className="w-full rounded-lg" data-testid="employment-status-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog (admin) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="employment-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit employment record</DialogTitle>
            <DialogDescription>Changing the joining date recalculates the locking end date. Every change is logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date selected</Label>
                <Input type="date" data-testid="edit-date-selected-input" value={(edit.date_selected || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, date_selected: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Date of joining</Label>
                <Input type="date" data-testid="edit-joining-date-input" value={(edit.date_of_joining || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, date_of_joining: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Textarea data-testid="edit-remarks-input" rows={2} value={edit.remarks || ""} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} />
            </div>
            <Button onClick={submitEdit} disabled={busy} className="w-full rounded-lg" data-testid="employment-edit-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
